/**
 * TimeInput — in-page time selector for attendance forms.
 *
 * Replaces <input type="time"> which opens the phone's native time-picker
 * dialog that is clipped on large-display / large-font Android devices,
 * making the SET button unreachable.
 *
 * UX:
 *  - AM / PM toggle
 *  - Hour chips  1–12  (6 × 2 grid)
 *  - Minute chips 00 05 10 … 55  (6 × 2 grid) + free-entry input for odd minutes
 *  - Large-tap targets (min 32 px) — safe for phone use
 *
 * API:
 *  value    — "HH:MM" 24-hour string, or "" / undefined when empty
 *  onChange — called with "HH:MM" 24-hour string when the user picks anything
 *  testid   — forwarded as data-testid on the root <div>
 *  label    — optional short label shown next to the clock icon
 */
import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

const HOURS     = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MIN_CHIPS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

function parse24(v) {
  if (!v) return null;
  const rx = /^(\d{1,2}):(\d{2})$/.exec(String(v).trim());
  if (!rx) return null;
  const hh = +rx[1], mm = +rx[2];
  if (hh > 23 || mm > 59) return null;
  return { h12: hh % 12 || 12, m: mm, ampm: hh < 12 ? 'AM' : 'PM' };
}

function fmt24(h12, m, ampm) {
  const hh = (h12 % 12) + (ampm === 'PM' ? 12 : 0);
  return `${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function TimeInput({ value, onChange, testid, label }) {
  const p = parse24(value);

  // Internal state — initialised from prop, kept in sync by useEffect
  const [h, setH] = useState(p?.h12 ?? 9);
  const [m, setM] = useState(p?.m ?? 0);
  const [ap, setAp] = useState(p?.ampm ?? 'AM');
  // Whether the user (or caller) has ever set a time
  const [active, setActive] = useState(!!value);
  // Free-minute text buffer
  const [freeMin, setFreeMin] = useState('');

  useEffect(() => {
    if (value) {
      const pp = parse24(value);
      if (pp) { setH(pp.h12); setM(pp.m); setAp(pp.ampm); setActive(true); }
    } else {
      setActive(false);
    }
  }, [value]);

  function emit(nh, nm, nap) {
    setActive(true);
    onChange(fmt24(nh, nm, nap));
  }

  const pickHour = (nh) => { setH(nh);  emit(nh, m, ap);  };
  const pickMin  = (nm) => { setM(nm);  emit(h, nm, ap);  };
  const pickAmpm = (na) => { setAp(na); emit(h, m, na);   };

  function commitFree() {
    const n = parseInt(freeMin, 10);
    if (!isNaN(n) && n >= 0 && n <= 59) pickMin(n);
    setFreeMin('');
  }

  // Chip styles
  const chip = 'rounded text-xs font-bold py-1 text-center cursor-pointer border transition-colors select-none focus:outline-none';
  const on   = 'bg-[#3b4872] text-white border-[#3b4872]';
  const off  = 'bg-slate-50 text-slate-700 border-slate-200 hover:border-[#3b4872] hover:text-[#3b4872]';

  const display = active
    ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ap}`
    : null;

  return (
    <div
      className="border border-slate-200 rounded-lg p-2.5 bg-white space-y-2"
      data-testid={testid}
    >
      {/* Header: label + live display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
          <Clock size={11} />
          <span>{label || 'Time'}</span>
        </div>
        <span
          className={`font-display font-bold text-sm tabular-nums ${active ? 'text-[#3b4872]' : 'text-slate-300'}`}
          data-testid={testid ? `${testid}-display` : undefined}
        >
          {display || '—  :  —'}
        </span>
      </div>

      {/* AM / PM */}
      <div className="grid grid-cols-2 gap-1" data-testid={testid ? `${testid}-ampm` : undefined}>
        {['AM', 'PM'].map((a) => (
          <button
            key={a} type="button"
            onClick={() => pickAmpm(a)}
            className={`${chip} py-1.5 ${a === ap && active ? on : off}`}
            data-testid={testid ? `${testid}-${a.toLowerCase()}` : undefined}
          >
            {a}
          </button>
        ))}
      </div>

      {/* Hours */}
      <div>
        <div className="text-[10px] font-bold uppercase text-slate-400 mb-1">Hour</div>
        <div className="grid grid-cols-6 gap-1" data-testid={testid ? `${testid}-hours` : undefined}>
          {HOURS.map((hr) => (
            <button
              key={hr} type="button"
              onClick={() => pickHour(hr)}
              className={`${chip} ${h === hr && active ? on : off}`}
              data-testid={testid ? `${testid}-hr-${hr}` : undefined}
            >
              {hr}
            </button>
          ))}
        </div>
      </div>

      {/* Minutes */}
      <div>
        <div className="text-[10px] font-bold uppercase text-slate-400 mb-1">Minute</div>
        <div className="grid grid-cols-6 gap-1 mb-1.5" data-testid={testid ? `${testid}-minutes` : undefined}>
          {MIN_CHIPS.map((mn) => (
            <button
              key={mn} type="button"
              onClick={() => pickMin(mn)}
              className={`${chip} ${m === mn && active ? on : off}`}
              data-testid={testid ? `${testid}-min-${mn}` : undefined}
            >
              {String(mn).padStart(2, '0')}
            </button>
          ))}
        </div>
        {/* Free-entry for non-5-minute values */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 whitespace-nowrap">or type:</span>
          <input
            type="number" min="0" max="59"
            value={freeMin}
            onChange={(e) => setFreeMin(e.target.value)}
            onBlur={commitFree}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitFree(); } }}
            placeholder="0–59"
            className="w-16 px-2 py-0.5 border border-slate-200 rounded text-xs text-slate-800 focus:outline-none focus:border-[#3b4872]"
            data-testid={testid ? `${testid}-free-min` : undefined}
          />
        </div>
      </div>
    </div>
  );
}
